import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { onSnapshot, collection, query, where, orderBy, Timestamp, limit } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { Conversation, Message, UserModel } from '../types';
import { useAuth } from './AuthContext';
import { User } from 'firebase/auth';
// import { withErrorHandling } from '../lib/errors'; // Reserved for future use
import { usePageVisibility } from '../hooks/usePageVisibility';

import { sanitizeString, sanitizeError } from '../utils/sanitization';
import { isValidDocumentId, validateString } from '../utils/validation';
import { debounce } from '../utils/debounce';
import { requestDeduplicator } from '../utils/requestDeduplication';

// Security: Enhanced search term sanitization for messaging-specific needs
function sanitizeSearchTerm(term: string): string {
  // Security: Enhanced sanitization with comprehensive character filtering
  return term
    .replace(/[<>"'&]/g, '') // Remove HTML/script characters
    .replace(/[^\w\s-]/g, '') // Remove special characters except word chars, spaces, and hyphens
    .trim()
    .slice(0, 50); // Limit search term length
}

// Security: Validate and sanitize user profile data
function sanitizeUserProfile(profile: any): UserModel | null {
  if (!profile || typeof profile !== 'object') return null;
  
  try {
    return {
      id: isValidDocumentId(profile.id) ? profile.id : '',
      uid: isValidDocumentId(profile.uid) ? profile.uid : '',
      firstName: sanitizeString(profile.firstName, 50) || '',
      lastName: sanitizeString(profile.lastName, 50) || '',
      displayName: sanitizeString(profile.displayName, 100) || '',
      email: sanitizeString(profile.email, 254) || '',
      phoneNumber: sanitizeString(profile.phoneNumber, 20) || '',
      photoURL: sanitizeString(profile.photoURL, 500) || '',
      role: ['user', 'seller', 'admin'].includes(profile.role) ? profile.role : 'user',
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt
    } as UserModel;
  } catch (error) {
    return null;
  }
}

// Security: Enhanced rate limiting for messaging operations
// Note: This could be consolidated with CartRateLimiter in CartContext if rate limits remain the same
class ContextRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 50; // 50 operations per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 500; // 500 operations per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many messaging operations in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many messaging operations. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }

  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

const contextRateLimiter = new ContextRateLimiter();

// Extended Message interface for optimistic updates
interface MessageWithStatus extends Message {
  status?: 'sending' | 'sent' | 'failed';
  tempId?: string;
}

interface MessagingContextType {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  error: string | null;
  isOtherUserTyping: boolean;
  isOnline: boolean;
  isSending: boolean;
  isInitialized: boolean;
  // Pagination state
  hasMore: boolean;
  loadingMore: boolean;
  // Typing state
  isTyping: boolean;
  // Filtering state
  filteredConversations: Conversation[];
  // Functions
  setSelectedConversation: (conversation: Conversation | null) => void;
  sendMessage: (text: string, file?: File) => Promise<void>;
  markConversationRead: (conversationId: string, uid: string) => Promise<void>;
  getOrCreateConversation: (otherUserId: string) => Promise<Conversation | null>;
  getUnreadCount: () => number;
  loadConversations: () => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  handleTyping: (isTyping: boolean) => void;
  clearError: () => void;
  clearMessageCache: (conversationId?: string) => void;
  retryFailedMessage: (messageId: string) => Promise<void>;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export const MessagingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<MessageWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const visible = usePageVisibility();
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Pagination state
  // const [lastLoadedAt, setLastLoadedAt] = useState<Timestamp | null>(null); // Reserved for future use
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Typing state
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Safely get currentUser with error handling
  let currentUser: User | null = null;
  try {
    const authContext = useAuth();
    currentUser = authContext.currentUser;
  } catch (error) {
    // If useAuth is not available yet, currentUser will be null
    // SECURITY: Removed console.warn to prevent information leakage
  }
  
  // Cache for user profiles to avoid repeated fetches (reserved for future use)
  // const userProfileCache = useRef(new Map<string, UserModel>());
  const conversationsListenerRef = useRef<(() => void) | null>(null);
  const messagesListenerRef = useRef<(() => void) | null>(null);
  const lastLoadedAtRef = useRef<Timestamp | null>(null);
  
  // PERFORMANCE: Debounced conversation update handler
  const debouncedUpdateConversations = useRef<ReturnType<typeof debounce> | null>(null);
  
  // Message cache for faster loading
  const messageCache = useRef(new Map<string, MessageWithStatus[]>());
  const cacheTimestamps = useRef(new Map<string, number>());
  // const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes (reserved for future use)

  // Security: Filtering state with sanitized search
  const [filterFlags] = useState({
    unreadOnly: false,
    storeOnly: false,
    muted: false,
    search: ''
  });

  // Performance: Memoized filtered conversations with optimized filtering
  const filteredConversations = useMemo(() => {
    let filtered = conversations;

    // Apply unread filter
    if (filterFlags.unreadOnly) {
      filtered = filtered.filter(conv => (conv.unreadCount || 0) > 0);
    }

    // Apply store filter (conversations with sellers)
    if (filterFlags.storeOnly) {
      filtered = filtered.filter(conv => conv.otherUser?.role === 'seller');
    }

    // Apply muted filter
    if (filterFlags.muted) {
      filtered = filtered.filter(conv => 
        conv.mutedBy?.includes(currentUser?.uid || '')
      );
    }

    // Security: Apply search filter with sanitization
    if (filterFlags.search) {
      const searchTerm = sanitizeSearchTerm(filterFlags.search).toLowerCase();
      if (searchTerm) {
        filtered = filtered.filter(conv => {
          try {
            const displayName = (conv.otherUser?.displayName || '').toLowerCase();
            const lastMessage = (conv.lastMessage || '').toLowerCase();
            return displayName.includes(searchTerm) || lastMessage.includes(searchTerm);
          } catch (error) {
            // Security: Handle search errors gracefully
            return false;
          }
        });
      }
    }

    return filtered;
  }, [conversations, filterFlags, currentUser?.uid]);
  
  // Clear cache function
  const clearMessageCache = useCallback((conversationId?: string) => {
    if (conversationId) {
      messageCache.current.delete(conversationId);
      cacheTimestamps.current.delete(conversationId);
    } else {
      messageCache.current.clear();
      cacheTimestamps.current.clear();
    }
  }, []);


  // Security: Enhanced load conversations with better error handling
  const loadConversations = useCallback(async () => {
    if (!currentUser?.uid) {
      // Security: Remove sensitive logging
      setConversations([]);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    // Security: Enhanced user ID validation
    const userIdValidation = validateString(currentUser.uid, 'User ID', 1, 1500);
    if (!userIdValidation.valid) {
      setError(userIdValidation.message || 'Invalid user ID');
      setConversations([]);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    if (!isValidDocumentId(currentUser.uid)) {
      setError('Invalid user ID format');
      setConversations([]);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    // Security: Enhanced rate limiting
    const rateLimitKey = `load_conversations_${currentUser.uid}`;
    const rateLimitResult = contextRateLimiter.isAllowed(rateLimitKey);
    if (!rateLimitResult.allowed) {
      setError(rateLimitResult.message || 'Too many requests. Please try again later.');
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { messagingService } = await import('../services/firebaseConfig');
      const conversationsData = await messagingService.getConversationsByUser();
      
      // Check for and cleanup duplicate conversations
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        await messagingService.cleanupDuplicateConversations();
        // Reload conversations after cleanup
        const cleanedConversations = await messagingService.getConversationsByUser();
        setConversations(cleanedConversations);
      } catch (cleanupError) {
        // Security: Sanitize cleanup error
        // SECURITY: Removed console.warn to prevent information leakage
        // Use original conversations if cleanup fails
        setConversations(conversationsData);
      }
    } catch (err: any) {
      // Security: Sanitize error messages
      const errorMessage = sanitizeError(err);
      setError(errorMessage);
      // Set empty conversations on error to prevent infinite loading
      setConversations([]);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [currentUser?.uid]);

  // Don't auto-load conversations - only load when explicitly called
  useEffect(() => {
    if (!currentUser?.uid) {
      // Set initialized even when no user to prevent infinite loading
      setIsInitialized(true);
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Setup real-time conversations listener with enhanced error handling
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Try with updatedAt first, fallback to lastMessageTime if index doesn't exist
    // NOTE: Composite index required: conversations [participants: array-contains] + [updatedAt: desc]
    // Covered in firestore.indexes.json
    let conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      conversationsQuery,
      async (snapshot) => {
        const rawConversations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Conversation[];

        // Processing real-time conversations

        const currentUserId = currentUser?.uid;
        if (!currentUserId) {
          // SECURITY: Removed console.warn to prevent information leakage
          setConversations(rawConversations);
          setLoading(false);
          setError(null);
          setIsInitialized(true);
          return;
        }

        // Security: Collect all unique other user IDs with validation
        const otherUserIds = new Set<string>();
        rawConversations.forEach(conv => {
          if (conv.participants && Array.isArray(conv.participants)) {
            conv.participants.forEach(uid => {
              // Security: Validate user IDs before adding
              if (uid !== currentUserId && isValidDocumentId(uid)) {
                otherUserIds.add(uid);
              }
            });
          }
        });

        // PERFORMANCE: Use request deduplication for user profile fetching
        // This prevents duplicate requests when listener fires multiple times quickly
        let userProfilesMap = new Map<string, UserModel>();
        if (otherUserIds.size > 0) {
          const userIdsArray = Array.from(otherUserIds).sort().join(',');
          const dedupeKey = `user_profiles_${currentUserId}_${userIdsArray}`;
          
          try {
            const { messagingService } = await import('../services/firebaseConfig');
            
            // Use request deduplication to prevent duplicate concurrent requests
            const rawProfiles = await requestDeduplicator.deduplicate(
              dedupeKey,
              () => messagingService.getBatchUserProfiles(Array.from(otherUserIds))
            );
            
            // Security: Sanitize all user profiles
            rawProfiles.forEach((profile, uid) => {
              const sanitizedProfile = sanitizeUserProfile(profile);
              if (sanitizedProfile) {
                userProfilesMap.set(uid, sanitizedProfile);
              }
            });
          } catch (error) {
            // Security: Sanitize error and create fallback profiles
            // SECURITY: Removed console.warn to prevent information leakage
            // Create fallback profiles
            otherUserIds.forEach(uid => {
              userProfilesMap.set(uid, {
                id: uid,
                uid: uid,
                firstName: 'Unknown',
                lastName: 'User',
                displayName: 'Unknown User',
                email: '',
                phoneNumber: '',
                photoURL: '',
                role: 'user' as const,
                createdAt: undefined,
                updatedAt: undefined
              });
            });
          }
        }

        // Security: Enrich conversations with otherUser profiles
        const enrichedConversations = rawConversations.map(conv => {
          const otherUserId = conv.participants?.find(uid => uid !== currentUserId);
          if (otherUserId && isValidDocumentId(otherUserId)) {
            const otherUser = userProfilesMap.get(otherUserId);
            if (otherUser) {
              return { ...conv, otherUser };
            } else {
              // Security: Create fallback profile with sanitized data
              return {
                ...conv,
                otherUser: {
                  id: otherUserId,
                  uid: otherUserId,
                  firstName: 'Unknown',
                  lastName: 'User',
                  displayName: 'Unknown User',
                  email: '',
                  phoneNumber: '',
                  photoURL: '',
                  role: 'user' as const,
                  createdAt: undefined,
                  updatedAt: undefined
                }
              };
            }
          }
          return conv;
        });

        // Security: Real-time conversations update completed
        setConversations(enrichedConversations);
        setLoading(false);
        setError(null);
        setIsInitialized(true);
      },
      (error) => {
        // Security: If updatedAt index doesn't exist, try with lastMessageTime
        if (error.message.includes('index')) {
          // SECURITY: Removed console.warn to prevent information leakage
          // NOTE: Composite index required: conversations [participants: array-contains] + [lastMessageTime: desc]
          // Covered in firestore.indexes.json
          if (!currentUser?.uid) return;
          const fallbackQuery = query(
            collection(db, 'conversations'),
            where('participants', 'array-contains', currentUser.uid),
            orderBy('lastMessageTime', 'desc')
          );
          
          const fallbackUnsubscribe = onSnapshot(
            fallbackQuery,
            async (snapshot) => {
              const rawConversations = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              })) as Conversation[];

              const currentUserId = currentUser?.uid;
              if (!currentUserId) {
                setConversations(rawConversations);
                setLoading(false);
                setError(null);
                setIsInitialized(true);
                return;
              }

              // Security: Collect all unique other user IDs with validation
              const otherUserIds = new Set<string>();
              rawConversations.forEach(conv => {
                if (conv.participants && Array.isArray(conv.participants)) {
                  conv.participants.forEach(uid => {
                    // Security: Validate user IDs before adding
                    if (uid !== currentUserId && isValidDocumentId(uid)) {
                      otherUserIds.add(uid);
                    }
                  });
                }
              });

              // Security: Fetch profiles for all other users in batch with sanitization
              let userProfilesMap = new Map<string, UserModel>();
              if (otherUserIds.size > 0) {
                try {
                  const { messagingService } = await import('../services/firebaseConfig');
                  const rawProfiles = await messagingService.getBatchUserProfiles(Array.from(otherUserIds));
                  
                  // Security: Sanitize all user profiles
                  rawProfiles.forEach((profile, uid) => {
                    const sanitizedProfile = sanitizeUserProfile(profile);
                    if (sanitizedProfile) {
                      userProfilesMap.set(uid, sanitizedProfile);
                    }
                  });
                } catch (error) {
                  // Security: Create fallback profiles
                  otherUserIds.forEach(uid => {
                    userProfilesMap.set(uid, {
                      id: uid,
                      uid: uid,
                      firstName: 'Unknown',
                      lastName: 'User',
                      displayName: 'Unknown User',
                      email: '',
                      phoneNumber: '',
                      photoURL: '',
                      role: 'user' as const,
                      createdAt: undefined,
                      updatedAt: undefined
                    });
                  });
                }
              }

              // Security: Enrich conversations with otherUser profiles
              const enrichedConversations = rawConversations.map(conv => {
                const otherUserId = conv.participants?.find(uid => uid !== currentUserId);
                if (otherUserId && isValidDocumentId(otherUserId)) {
                  const otherUser = userProfilesMap.get(otherUserId);
                  if (otherUser) {
                    return { ...conv, otherUser };
                  } else {
                    // Security: Create fallback profile with sanitized data
                    return {
                      ...conv,
                      otherUser: {
                        id: otherUserId,
                        uid: otherUserId,
                        firstName: 'Unknown',
                        lastName: 'User',
                        displayName: 'Unknown User',
                        email: '',
                        phoneNumber: '',
                        photoURL: '',
                        role: 'user' as const,
                        createdAt: undefined,
                        updatedAt: undefined
                      }
                    };
                  }
                }
                return conv;
              });

              setConversations(enrichedConversations);
              setLoading(false);
              setError(null);
              setIsInitialized(true);
            },
            (fallbackError) => {
              // Security: Sanitize error message
              // SECURITY: Removed console.warn to prevent information leakage
              // Final fallback: query without ordering
              // NOTE: No composite index needed - only array-contains filter, no orderBy
              if (!currentUser?.uid) return;
              const finalQuery = query(
                collection(db, 'conversations'),
                where('participants', 'array-contains', currentUser.uid)
              );
              
              const finalUnsubscribe = onSnapshot(
                finalQuery,
                async (snapshot) => {
                  const rawConversations = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  })) as Conversation[];

                  const currentUserId = currentUser?.uid;
                  if (!currentUserId) {
                    setConversations(rawConversations);
                    setLoading(false);
                    setError(null);
                    setIsInitialized(true);
                    return;
                  }

                  // Security: Collect all unique other user IDs with validation
                  const otherUserIds = new Set<string>();
                  rawConversations.forEach(conv => {
                    if (conv.participants && Array.isArray(conv.participants)) {
                      conv.participants.forEach(uid => {
                        // Security: Validate user IDs before adding
                        if (uid !== currentUserId && isValidDocumentId(uid)) {
                          otherUserIds.add(uid);
                        }
                      });
                    }
                  });

                  // PERFORMANCE: Use request deduplication for user profile fetching
                  let userProfilesMap = new Map<string, UserModel>();
                  if (otherUserIds.size > 0) {
                    const userIdsArray = Array.from(otherUserIds).sort().join(',');
                    const dedupeKey = `user_profiles_${currentUserId}_${userIdsArray}`;
                    
                    try {
                      const { messagingService } = await import('../services/firebaseConfig');
                      
                      // Use request deduplication to prevent duplicate concurrent requests
                      const rawProfiles = await requestDeduplicator.deduplicate(
                        dedupeKey,
                        () => messagingService.getBatchUserProfiles(Array.from(otherUserIds))
                      );
                      
                      // Security: Sanitize all user profiles
                      rawProfiles.forEach((profile, uid) => {
                        const sanitizedProfile = sanitizeUserProfile(profile);
                        if (sanitizedProfile) {
                          userProfilesMap.set(uid, sanitizedProfile);
                        }
                      });
                    } catch (error) {
                      // Security: Create fallback profiles
                      otherUserIds.forEach(uid => {
                        userProfilesMap.set(uid, {
                          id: uid,
                          uid: uid,
                          firstName: 'Unknown',
                          lastName: 'User',
                          displayName: 'Unknown User',
                          email: '',
                          phoneNumber: '',
                          photoURL: '',
                          role: 'user' as const,
                          createdAt: undefined,
                          updatedAt: undefined
                        });
                      });
                    }
                  }

                  // Security: Enrich conversations with otherUser profiles
                  const enrichedConversations = rawConversations.map(conv => {
                    const otherUserId = conv.participants?.find(uid => uid !== currentUserId);
                    if (otherUserId && isValidDocumentId(otherUserId)) {
                      const otherUser = userProfilesMap.get(otherUserId);
                      if (otherUser) {
                        return { ...conv, otherUser };
                      } else {
                        // Security: Create fallback profile with sanitized data
                        return {
                          ...conv,
                          otherUser: {
                            id: otherUserId,
                            uid: otherUserId,
                            firstName: 'Unknown',
                            lastName: 'User',
                            displayName: 'Unknown User',
                            email: '',
                            phoneNumber: '',
                            photoURL: '',
                            role: 'user' as const,
                            createdAt: undefined,
                            updatedAt: undefined
                          }
                        };
                      }
                    }
                    return conv;
                  });

                  // Sort manually on client side
                  enrichedConversations.sort((a, b) => {
                    const aTime = a.updatedAt || a.lastMessageTime;
                    const bTime = b.updatedAt || b.lastMessageTime;
                    if (aTime && bTime) {
                      const aMillis = typeof aTime === 'object' && 'toMillis' in aTime ? aTime.toMillis() : aTime;
                      const bMillis = typeof bTime === 'object' && 'toMillis' in bTime ? bTime.toMillis() : bTime;
                      return bMillis - aMillis;
                    }
                    return 0;
                  });
                  setConversations(enrichedConversations);
                  setLoading(false);
                  setError(null);
                  setIsInitialized(true);
                },
                (finalError) => {
                  // SECURITY: Removed console.warn to prevent information leakage
                  setError(null);
                  setLoading(false);
                  setIsInitialized(true);
                  setConversations([]);
                }
              );
              
              conversationsListenerRef.current = finalUnsubscribe;
            }
          );
          
          conversationsListenerRef.current = fallbackUnsubscribe;
          return;
        }
        
        // Handle other Firestore errors silently to prevent blocking the app
        // SECURITY: Removed console.warn to prevent information leakage
        setError(null); // Don't show error to user
        setLoading(false);
        setIsInitialized(true); // Set initialized even on error to prevent infinite loading
        setConversations([]);
        
        // Don't propagate the error to avoid cascading UNKNOWN errors
        return;
      }
    );

    conversationsListenerRef.current = unsubscribe;
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Handle visibility changes without recreating the listener
  useEffect(() => {
    if (!conversationsListenerRef.current) return;
    
    // Removed verbose logging - visibility changes happen too frequently
    // The listener will continue running but we won't process updates when hidden
  }, [visible]);

  // Load messages for selected conversation with pagination and caching
  const loadMessages = useCallback(async (conversationId: string, reset = false) => {
    if (!currentUser?.uid) {
      return;
    }

    try {
      if (reset) {
        setMessages([]);
        lastLoadedAtRef.current = null;
        setHasMore(true);
      }

      // Simplified loading - let real-time listener handle updates
      // Only load initial messages if no real-time listener is active
      if (reset && !messagesListenerRef.current) {
        const { messagingService } = await import('../services/firebaseConfig');
        const newMessages = await messagingService.listMessages(conversationId, 30, null);

        setMessages(newMessages);
        messageCache.current.set(conversationId, newMessages);
        cacheTimestamps.current.set(conversationId, Date.now());

        // Update pagination state
        if (newMessages.length > 0) {
          const newLastLoadedAt = newMessages[newMessages.length - 1].timestamp;
          lastLoadedAtRef.current = newLastLoadedAt;
        }
        setHasMore(newMessages.length === 30);
      }
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
      setError('Failed to load messages');
    }
  }, [currentUser?.uid]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!selectedConversation?.id || !hasMore || loadingMore) return;

    setLoadingMore(true);
    try {
      await loadMessages(selectedConversation.id, false);
    } finally {
      setLoadingMore(false);
    }
  }, [selectedConversation?.id, hasMore, loadingMore, loadMessages]);

  // Load initial messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation?.id || !currentUser?.uid || !visible) {
      setMessages([]);
      lastLoadedAtRef.current = null;
      setHasMore(true);
      return;
    }

    // Refresh user profile for the selected conversation
    const refreshUserProfile = async () => {
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        const refreshedConversation = await messagingService.refreshConversationUserProfile(selectedConversation.id);
        if (refreshedConversation && refreshedConversation.otherUser) {
          // Update the conversation in the list with the refreshed user profile
          setConversations(prev => 
            prev.map(conv => 
              conv.id === selectedConversation.id 
                ? { ...conv, otherUser: refreshedConversation.otherUser }
                : conv
            )
          );
        }
      } catch (error) {
        // SECURITY: Removed console.error to prevent information leakage
      }
    };

    refreshUserProfile();
    loadMessages(selectedConversation.id, true);
  }, [selectedConversation?.id, currentUser?.uid, loadMessages]);

  // Real-time message listener for the selected conversation
  useEffect(() => {
    if (!selectedConversation?.id || !currentUser?.uid || !visible) {
      // Clean up existing listener
      if (messagesListenerRef.current) {
        messagesListenerRef.current();
        messagesListenerRef.current = null;
      }
      return;
    }

    // Removed verbose logging - message listeners happen too frequently
    // Set up real-time listener for messages
    const messagesQuery = query(
      collection(db, 'conversations', selectedConversation.id, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(50) // Limit to recent messages for performance
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const newMessages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Message[];

        // Removed verbose logging - message updates happen too frequently
        // Merge with optimistic messages and de-dup
        setMessages(prev => {
          const optimistic = prev.filter(m => m.status === 'sending' || m.status === 'failed');
          const merged = [...optimistic, ...newMessages];
          const unique = Array.from(new Map(merged.map(m => [(m as any).id || (m as any).tempId, m])).values());
          return unique.sort((a: any, b: any) => {
            const aTime = (a.timestamp as any)?.toMillis?.() || 0;
            const bTime = (b.timestamp as any)?.toMillis?.() || 0;
            return aTime - bTime;
          });
        });
        
        // Update cache
        messageCache.current.set(selectedConversation.id, newMessages);
        cacheTimestamps.current.set(selectedConversation.id, Date.now());
        
        // Update pagination state (newest at end with asc ordering)
        if (newMessages.length > 0) {
          const lastMessage = newMessages[0];
          lastLoadedAtRef.current = lastMessage.timestamp;
        }
        setHasMore(newMessages.length === 50);
      },
      (error) => {
        // SECURITY: Removed console.error to prevent information leakage
        // Fallback to manual loading if real-time fails
        loadMessages(selectedConversation.id, true);
      }
    );

    messagesListenerRef.current = unsubscribe;

    return () => {
      if (messagesListenerRef.current) {
        messagesListenerRef.current();
        messagesListenerRef.current = null;
      }
    };
  }, [selectedConversation?.id, currentUser?.uid, visible, loadMessages]);

  // Security: Enhanced send message with optimistic updates and status tracking
  const sendMessage = useCallback(async (text: string, file?: File) => {
    if (!currentUser?.uid || !selectedConversation?.id || (!text.trim() && !file)) return;

    try {
      // Security: Enhanced input validation
      const textValidation = text ? validateString(text, 'Message text', 1, 2000) : { valid: true };
      if (!textValidation.valid) {
        setError(textValidation.message || 'Invalid message text');
        return;
      }

      const conversationIdValidation = validateString(selectedConversation.id, 'Conversation ID', 1, 1500);
      if (!conversationIdValidation.valid) {
        setError(conversationIdValidation.message || 'Invalid conversation ID');
        return;
      }

      if (!isValidDocumentId(selectedConversation.id)) {
        setError('Invalid conversation ID format');
        return;
      }

      // Security: Enhanced rate limiting
      const rateLimitKey = `send_message_${currentUser.uid}`;
      const rateLimitResult = contextRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        setError(rateLimitResult.message || 'Too many messages. Please try again later.');
        return;
      }

      // Security: Data size limits
      const MAX_MESSAGES_PER_CONVERSATION = 1000;
      if (messages.length >= MAX_MESSAGES_PER_CONVERSATION) {
        setError('Too many messages in this conversation');
        return;
      }

      const tempId = `temp-${Date.now()}-${Math.random()}`;
      
      // Security: Sanitize message text
      const sanitizedText = sanitizeString(text, 2000) || '';
      
      // Create optimistic message with status tracking
      const optimisticMessage: MessageWithStatus = {
        id: tempId,
        tempId: tempId,
        conversationId: selectedConversation.id,
        senderId: currentUser.uid,
        text: sanitizedText,
        timestamp: (Timestamp.now() as any),
        readBy: [currentUser.uid],
        isRead: true,
        status: 'sending'
      };

      // Removed verbose logging - message sending happens too frequently
      // Add optimistic message immediately for instant UI feedback
      setMessages(prev => [optimisticMessage, ...prev]);
      
      // Update cache optimistically
      const currentMessages = messageCache.current.get(selectedConversation.id) || [];
      messageCache.current.set(selectedConversation.id, [optimisticMessage, ...currentMessages]);
      cacheTimestamps.current.set(selectedConversation.id, Date.now());

      setIsSending(true);
      
      try {
        // Use the new messaging service
        const { messagingService } = await import('../services/messagingService');
        const result = await messagingService.sendMessage(selectedConversation.id, sanitizedText, { file });
        
        if (result.success && result.message) {
          // Mark optimistic message as sent (real-time listener will handle the actual update)
          setMessages(prev => prev.map(m => 
            m.tempId === tempId
              ? { ...m, status: 'sent' as const }
              : m
          ));
          
          // Update cache with sent status
          const cachedMessages = messageCache.current.get(selectedConversation.id) || [];
          const updatedCache = cachedMessages.map(m => 
            m.tempId === tempId
              ? { ...m, status: 'sent' as const }
              : m
          );
          messageCache.current.set(selectedConversation.id, updatedCache);
        } else {
          throw new Error(result.error || 'Failed to send message');
        }
      } catch (sendError: any) {
        // Security: Sanitize error messages
        const sanitizedError = sanitizeError(sendError);
        // SECURITY: Removed console.warn to prevent information leakage
        
        // Mark optimistic message as failed
        setMessages(prev => prev.map(m => 
          m.tempId === tempId
            ? { ...m, status: 'failed' as const }
            : m
        ));
        
        // Update cache with failed status
        const cachedMessages = messageCache.current.get(selectedConversation.id) || [];
        const updatedCache = cachedMessages.map(m => 
          m.tempId === tempId
            ? { ...m, status: 'failed' as const }
            : m
        );
        messageCache.current.set(selectedConversation.id, updatedCache);
        
        // Set error for user feedback
        setError(sanitizedError);
      } finally {
        setIsSending(false);
      }
    } catch (err: any) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(err);
      // SECURITY: Removed console.warn to prevent information leakage
      setError(sanitizedError);
      setIsSending(false);
    }
  }, [currentUser?.uid, selectedConversation?.id, messages]);

  // Retry failed message
  const retryFailedMessage = useCallback(async (messageId: string) => {
    if (!currentUser?.uid || !selectedConversation?.id) return;

    const failedMessage = messages.find(m => m.id === messageId || m.tempId === messageId);
    if (!failedMessage || failedMessage.status !== 'failed') return;

    // Mark as sending again
    setMessages(prev => prev.map(m => 
      m.id === messageId || m.tempId === messageId
        ? { ...m, status: 'sending' as const }
        : m
    ));

    try {
      const { messagingService } = await import('../services/firebaseConfig');
      const message = await messagingService.sendMessage(selectedConversation.id, failedMessage.text);
      
      if (message) {
        // Replace failed message with real message
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== messageId && m.tempId !== messageId);
          return [message, ...filtered];
        });
        
        // Update cache
        const cachedMessages = messageCache.current.get(selectedConversation.id) || [];
        const filteredCache = cachedMessages.filter(m => m.id !== messageId && m.tempId !== messageId);
        messageCache.current.set(selectedConversation.id, [message, ...filteredCache]);
        cacheTimestamps.current.set(selectedConversation.id, Date.now());
      }
    } catch (err: any) {
      // SECURITY: Removed console.error to prevent information leakage
      
      // Mark as failed again
      setMessages(prev => prev.map(m => 
        m.id === messageId || m.tempId === messageId
          ? { ...m, status: 'failed' as const }
          : m
      ));
      
      setError(err.message || 'Failed to retry message');
    }
  }, [currentUser?.uid, selectedConversation?.id, messages]);

  // Mark conversation as read
  const markConversationRead = useCallback(async (conversationId: string, uid: string) => {
    try {
      const { messagingService } = await import('../services/firebaseConfig');
      await messagingService.markConversationRead(conversationId, uid);
    } catch (err: any) {
      // SECURITY: Removed console.error to prevent information leakage
    }
  }, []);

  // Mark messages as read (legacy method for backward compatibility)
  const markMessagesAsRead = useCallback(async () => {
    if (!currentUser?.uid || !selectedConversation?.id) return;
    await markConversationRead(selectedConversation.id, currentUser.uid);
  }, [currentUser?.uid, selectedConversation?.id, markConversationRead]);

  // Get unread count
  const getUnreadCount = () => {
    return messages.filter(msg => !msg.readBy?.includes(currentUser?.uid || '')).length;
  };

  // Security: Enhanced conversation creation
  const getOrCreateConversation = useCallback(async (otherUserId: string) => {
    if (!currentUser?.uid) return null;

    try {
      // Security: Enhanced input validation
      const otherUserIdValidation = validateString(otherUserId, 'Other user ID', 1, 1500);
      if (!otherUserIdValidation.valid) {
        setError(otherUserIdValidation.message || 'Invalid user ID');
        return null;
      }

      if (!isValidDocumentId(otherUserId)) {
        setError('Invalid user ID format');
        return null;
      }

      // Security: Enhanced rate limiting
      const rateLimitKey = `create_conversation_${currentUser.uid}`;
      const rateLimitResult = contextRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        setError(rateLimitResult.message || 'Too many conversation requests. Please try again later.');
        return null;
      }

      // Removed verbose logging - conversation creation happens too frequently

      const { messagingService } = await import('../services/firebaseConfig');
      return await messagingService.getOrCreateConversation(otherUserId);
    } catch (err: any) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(err);
        // SECURITY: Removed console.warn to prevent information leakage
      setError(sanitizedError);
      return null;
    }
  }, [currentUser?.uid]);

  // Typing functionality with throttling
  const handleTyping = useCallback((isTyping: boolean) => {
    if (!selectedConversation?.id || !currentUser?.uid) return;

    const conversationId = selectedConversation.id;
    const userId = currentUser.uid;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (isTyping) {
      setIsTyping(true);
      
      // Async function to handle typing start
      const startTyping = async () => {
        try {
          const { messagingService } = await import('../services/firebaseConfig');
          messagingService.setTypingWithUid(conversationId, userId, true);
        } catch (error) {
          // SECURITY: Removed console.error to prevent information leakage
        }
      };
      
      startTyping();
      
      // Set timeout to stop typing after 3 seconds of inactivity (throttled)
      typingTimeoutRef.current = setTimeout(async () => {
        setIsTyping(false);
        try {
          const { messagingService } = await import('../services/firebaseConfig');
          messagingService.setTypingWithUid(conversationId, userId, false);
        } catch (error) {
          // SECURITY: Removed console.error to prevent information leakage
        }
      }, 3000);
    } else {
      setIsTyping(false);
      
      // Async function to handle typing stop
      const stopTyping = async () => {
        try {
          const { messagingService } = await import('../services/firebaseConfig');
          messagingService.setTypingWithUid(conversationId, userId, false);
        } catch (error) {
          // SECURITY: Removed console.error to prevent information leakage
        }
      };
      
      stopTyping();
    }
  }, [selectedConversation?.id, currentUser?.uid]);

  // Check if other user is typing
  useEffect(() => {
    if (!selectedConversation?.typingUserIds || !currentUser?.uid) {
      setIsOtherUserTyping(false);
      return;
    }

    const currentUserId = currentUser.uid;
    const otherUserId = selectedConversation.participants.find(uid => uid !== currentUserId);
    if (otherUserId) {
      setIsOtherUserTyping(selectedConversation.typingUserIds.includes(otherUserId));
    }
  }, [selectedConversation?.typingUserIds, selectedConversation?.participants, currentUser?.uid]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fallback timeout to prevent infinite loading
  useEffect(() => {
    // Only set up timeout once on mount, not when isInitialized changes
    const timeout = setTimeout(() => {
      setIsInitialized(prev => {
        // Only update if still not initialized to prevent unnecessary updates
        if (!prev) {
          setLoading(false);
          setConversations([]); // Set empty conversations on timeout
          return true;
        }
        return prev;
      });
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - isInitialized guard is inside the timeout

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Automatically mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation?.id && currentUser?.uid) {
      // Small delay to ensure messages are loaded
      const timer = setTimeout(() => {
        markMessagesAsRead();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [selectedConversation?.id, currentUser?.uid, markMessagesAsRead]);

  // Cleanup all listeners and timeouts on unmount
  useEffect(() => {
    return () => {
      if (conversationsListenerRef.current) {
        conversationsListenerRef.current();
      }
      if (messagesListenerRef.current) {
        messagesListenerRef.current();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const value: MessagingContextType = {
    conversations,
    selectedConversation,
    messages,
    loading,
    error,
    isOtherUserTyping,
    isOnline,
    isSending,
    isInitialized,
    // Pagination state
    hasMore,
    loadingMore,
    // Typing state
    isTyping,
    // Filtering state
    filteredConversations,
    // Functions
    setSelectedConversation,
    sendMessage,
    markConversationRead,
    getOrCreateConversation,
    getUnreadCount,
    loadConversations,
    loadMoreMessages,
    handleTyping,
    clearError: () => setError(null),
    clearMessageCache,
    retryFailedMessage
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};
